import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, ArrowRight, Shield, Brain, FileText, BarChart3,
  AlertTriangle, Clock, CheckCircle, Search, Zap, Bell,
  Lock, Globe, ChevronRight, Users, Building2, DollarSign,
  Send, Star, Activity, Eye, Calculator, Target,
} from 'lucide-react';

const API_BASE = '';

/* ─── Trust Badge ──────────────────────────────────────────── */
const TrustBadge = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 px-4 py-2 bg-[#0f0f1a]/80 border border-gray-800 rounded-full">
    <Icon className="w-4 h-4 text-[#00ff88]" />
    <span className="text-xs text-gray-400 font-medium">{label}</span>
  </div>
);

/* ─── Feature Card (2x3 grid) ──────────────────────────────── */
const FeatureCard = ({ icon: Icon, title, description, accent = '#00ff88' }) => (
  <div className="group bg-[#0f0f1a]/80 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 hover:border-gray-700 transition-all duration-300 hover:transform hover:scale-[1.02] relative overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `linear-gradient(135deg, ${accent}05 0%, transparent 60%)` }} />
    <div className="relative z-10">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: accent + '15' }}>
        <Icon className="w-6 h-6" style={{ color: accent }} />
      </div>
      <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
      <p className="text-sm text-gray-400 leading-relaxed">{description}</p>
    </div>
  </div>
);

/* ─── Step Card ────────────────────────────────────────────── */
const StepCard = ({ number, title, description, icon: Icon }) => (
  <div className="relative bg-[#0f0f1a]/80 border border-gray-800 rounded-2xl p-6 text-center hover:border-gray-700 transition-all">
    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#00ff88]/20 to-[#00d4ff]/20 flex items-center justify-center mx-auto mb-4">
      <span className="text-2xl font-bold text-[#00ff88]">{number}</span>
    </div>
    <Icon className="w-6 h-6 text-[#00d4ff] mx-auto mb-3" />
    <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
    <p className="text-sm text-gray-400 leading-relaxed">{description}</p>
  </div>
);

/* ─── Pricing Tier ─────────────────────────────────────────── */
const PricingTier = ({ name, price, features, recommended, cta, onCtaClick }) => (
  <div className={`relative bg-[#0f0f1a] rounded-2xl p-8 border ${recommended ? 'border-[#00ff88]/50 scale-105 shadow-lg shadow-[#00ff88]/10' : 'border-gray-800'} transition-all hover:border-gray-700 flex flex-col`}>
    {recommended && (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#00ff88] text-black text-xs font-bold rounded-full whitespace-nowrap">
        RECOMMENDED
      </div>
    )}
    <div className="text-center mb-6">
      <h3 className="text-xl font-bold text-white mb-1">{name}</h3>
      <div className="mt-3">
        <span className="text-4xl font-bold text-[#00ff88]">${price}</span>
        <span className="text-gray-500 text-sm">/month</span>
      </div>
    </div>
    <ul className="space-y-3 mb-8 flex-1">
      {features.map((f, i) => (
        <li key={i} className="flex items-start gap-3 text-sm">
          <CheckCircle className="w-4 h-4 text-[#00ff88] mt-0.5 flex-shrink-0" />
          <span className="text-gray-300">{f}</span>
        </li>
      ))}
    </ul>
    <button
      onClick={onCtaClick}
      className={`w-full py-3 rounded-xl font-bold text-sm transition-all ${
        recommended ? 'bg-[#00ff88] text-black hover:bg-[#00cc6a]' : 'bg-gray-800 text-white hover:bg-gray-700'
      }`}
    >
      {cta}
    </button>
  </div>
);

/* ─── Trust Item ───────────────────────────────────────────── */
const TrustItem = ({ icon: Icon, title, description }) => (
  <div className="bg-[#0f0f1a]/80 border border-gray-800 rounded-2xl p-6 hover:border-gray-700 transition-all text-center">
    <div className="w-12 h-12 rounded-xl bg-[#00d4ff]/10 flex items-center justify-center mx-auto mb-4">
      <Icon className="w-6 h-6 text-[#00d4ff]" />
    </div>
    <h4 className="text-white font-bold mb-2">{title}</h4>
    <p className="text-sm text-gray-400 leading-relaxed">{description}</p>
  </div>
);


/* ═══════════════════════════════════════════════════════════════
   ADVISORS PAGE
   ═══════════════════════════════════════════════════════════════ */
export default function AdvisorsPage() {
  const navigate = useNavigate();

  /* ─── Form State ─────────────────────────────────────────── */
  const [form, setForm] = useState({ full_name: '', email: '', firm_name: '', aum_range: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/advisors/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Submission failed');
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToDemo = () => {
    document.getElementById('advisor-contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#0a0a12] text-white overflow-x-hidden">

      {/* ═══ NAV ═══ */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a12]/90 backdrop-blur-md border-b border-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#00ff88] to-[#00d4ff] flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-black" />
            </div>
            <span className="text-lg font-bold">PROFIT<span className="text-[#00ff88]">SPOT</span></span>
          </button>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-gray-500 border border-gray-800 px-2 py-1 rounded-full">For Advisors</span>
            <button onClick={scrollToDemo} className="px-5 py-2 bg-[#00ff88] text-black text-sm font-bold rounded-lg hover:bg-[#00cc6a] transition-all">
              Schedule Demo
            </button>
          </div>
        </div>
      </nav>


      {/* ═══ SECTION 1: HERO ═══ */}
      <section className="pt-32 pb-20 px-4 relative">
        {/* Gradient glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-gradient-radial from-[#00ff88]/8 to-transparent rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#00ff88]/10 border border-[#00ff88]/20 rounded-full mb-6">
            <Building2 className="w-4 h-4 text-[#00ff88]" />
            <span className="text-sm text-[#00ff88] font-medium">Built for RIA Firms & Financial Advisors</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            DeFi Yield Intelligence<br />
            <span className="text-[#00ff88]">for Financial Advisors</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto mb-10 leading-relaxed">
            Research, analyze, and report on DeFi yield opportunities with the same rigor your clients expect from traditional fixed income — powered by institutional-grade data across 86+ chains and 6,500+ pools.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <button onClick={scrollToDemo} className="group inline-flex items-center gap-2 px-8 py-4 bg-[#00ff88] text-black text-base font-bold rounded-xl hover:bg-[#00cc6a] transition-all hover:scale-105">
              Schedule a Demo
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => navigate('/explore')} className="inline-flex items-center gap-2 px-8 py-4 bg-gray-800/60 text-white text-base font-bold rounded-xl hover:bg-gray-700 transition-all border border-gray-700">
              <Eye className="w-5 h-5" />
              Explore the Platform
            </button>
          </div>
          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            <TrustBadge icon={Shield} label="SEC-Compliant Reporting" />
            <TrustBadge icon={Globe} label="Multi-Chain (86+ Networks)" />
            <TrustBadge icon={Activity} label="Real-Time Data" />
            <TrustBadge icon={Lock} label="SOC 2 Roadmap" />
          </div>
        </div>
      </section>


      {/* ═══ SECTION 2: THE PROBLEM ═══ */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#0a0a12] to-[#0f0f1a]/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#ffa502]/10 border border-[#ffa502]/20 rounded-full mb-5">
              <AlertTriangle className="w-4 h-4 text-[#ffa502]" />
              <span className="text-sm text-[#ffa502] font-medium">The Advisor's Dilemma</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Your Clients Are Asking About <span className="text-[#00ff88]">DeFi Yields</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">And the tools to answer them professionally didn't exist — until now.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: TrendingUp,
                title: 'DeFi Outperforms Traditional Fixed Income',
                stat: '$180B+',
                statLabel: 'Total Value Locked',
                description: 'Stablecoin yields consistently exceed Treasury rates. Your clients are noticing — and asking for allocation strategies.',
                accent: '#00ff88',
              },
              {
                icon: AlertTriangle,
                title: 'Risk Assessment Is Fragmented',
                stat: '6,500+',
                statLabel: 'Yield Pools Across 86 Chains',
                description: 'No single platform aggregates DeFi risk data the way advisors need it: protocol audits, TVL trends, impermanent loss projections, smart contract grades.',
                accent: '#ffa502',
              },
              {
                icon: Users,
                title: 'HNW Clients Expect Answers',
                stat: '73%',
                statLabel: 'HNW Interest (Fidelity 2024)',
                description: 'Fidelity\'s 2024 Digital Assets Survey found 73% of high-net-worth investors have or want exposure to digital assets. They need advisors who can guide them.',
                accent: '#00d4ff',
              },
            ].map((item, i) => (
              <div key={i} className="bg-[#0f0f1a]/80 border border-gray-800 rounded-2xl p-6 hover:border-gray-700 transition-all">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: item.accent + '15' }}>
                  <item.icon className="w-6 h-6" style={{ color: item.accent }} />
                </div>
                <h3 className="text-lg font-bold text-white mb-3">{item.title}</h3>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="text-2xl font-bold" style={{ color: item.accent }}>{item.stat}</span>
                  <span className="text-xs text-gray-500">{item.statLabel}</span>
                </div>
                <p className="text-sm text-gray-400 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ═══ SECTION 3: WHAT PROFITSPOT GIVES ADVISORS ═══ */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#00d4ff]/10 border border-[#00d4ff]/20 rounded-full mb-5">
              <Zap className="w-4 h-4 text-[#00d4ff]" />
              <span className="text-sm text-[#00d4ff] font-medium">Purpose-Built for Advisory</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              What ProfitSpot Gives <span className="text-[#00ff88]">Advisors</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Six integrated tools designed for due diligence, client reporting, and portfolio construction.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard
              icon={Search}
              title="Yield Explorer"
              description="Screen 6,500+ pools across 86 chains. Filter by risk grade, TVL, protocol, APY range, and chain. Institutional-grade data from DeFiLlama, CoinGecko, and on-chain sources."
              accent="#00ff88"
            />
            <FeatureCard
              icon={Brain}
              title="AI Intelligence"
              description="Context-aware AI analysis for any pool or DeFi question. Compare pools, assess risks, and get natural-language summaries your clients can understand."
              accent="#00d4ff"
            />
            <FeatureCard
              icon={FileText}
              title="PDF Client Reports"
              description="Generate branded, audit-ready PDF reports for any pool — including yield breakdown, ProfitSpot Score, IL projections, break-even timeline, and risk summary."
              accent="#ffa502"
            />
            <FeatureCard
              icon={Calculator}
              title="IL Simulator"
              description="Model impermanent loss scenarios before recommending any LP position. Visualize outcomes across price movements, holding periods, and fee structures."
              accent="#ff6b6b"
            />
            <FeatureCard
              icon={Target}
              title="Forecast Engine"
              description="Monte Carlo simulations (1,000 iterations) generate probability distributions for any pool. ProfitSpot Score, risk zones, and median ROI projections."
              accent="#a855f7"
            />
            <FeatureCard
              icon={Bell}
              title="Real-Time Alerts"
              description="Set APY drop alerts, TVL thresholds, and rebalance triggers for monitored positions. Never miss a material change in your clients' DeFi allocations."
              accent="#00ff88"
            />
          </div>
        </div>
      </section>


      {/* ═══ SECTION 4: HOW ADVISORS USE IT ═══ */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#0a0a12] to-[#0f0f1a]/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              From Research to Client Report <span className="text-[#00ff88]">in Minutes</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">A streamlined workflow designed for how financial advisors actually work.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-[4.5rem] left-[20%] right-[20%] h-[2px] bg-gradient-to-r from-[#00ff88]/30 via-[#00d4ff]/30 to-[#00ff88]/30" />
            <StepCard
              number="1"
              title="Research"
              description="Screen thousands of pools by risk grade, chain, protocol, and yield range. Apply advisor-grade filters to surface only positions that meet your due diligence standards."
              icon={Search}
            />
            <StepCard
              number="2"
              title="Analyze"
              description="Run Monte Carlo forecasts, model IL scenarios, and query AI for natural-language risk assessments. Get the full picture before making any recommendation."
              icon={BarChart3}
            />
            <StepCard
              number="3"
              title="Report"
              description="Generate branded PDF reports with yield breakdowns, risk scores, and projections. Deliver professional documentation your compliance team and clients expect."
              icon={FileText}
            />
          </div>
        </div>
      </section>


      {/* ═══ SECTION 5: ENTERPRISE PRICING ═══ */}
      <section className="py-20 px-4" id="advisor-pricing">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Enterprise <span className="text-[#00ff88]">Pricing</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Flexible plans built for advisory practices. Start with Pro and upgrade as your DeFi practice grows.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <PricingTier
              name="Advisor Pro"
              price={29}
              features={[
                'Full Yield Explorer (6,500+ pools)',
                'AI Intelligence (unlimited queries)',
                'IL Simulator & Forecast Engine',
                'Portfolio Risk Analysis',
                'Real-time alerts (up to 10)',
                'Whale movement tracker',
                'Email support',
              ]}
              cta="Start Free Trial"
              onCtaClick={() => navigate('/explore')}
            />
            <PricingTier
              name="Advisor Elite"
              price={79}
              recommended
              features={[
                'Everything in Advisor Pro',
                'Branded PDF Client Reports',
                'Priority AI (faster, deeper analysis)',
                'Whale Tracker (full access)',
                'Unlimited alerts',
                'Dedicated support channel',
                'Forecast Dial (Monte Carlo)',
                'Custom risk thresholds',
              ]}
              cta="Schedule Demo"
              onCtaClick={scrollToDemo}
            />
          </div>

          <div className="text-center mt-10">
            <p className="text-sm text-gray-500 border border-gray-800 inline-block px-5 py-2 rounded-full">
              Coming soon: team seats, white-label reports, and API access
            </p>
          </div>
        </div>
      </section>


      {/* ═══ SECTION 6: TRUST & COMPLIANCE ═══ */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#0a0a12] to-[#0f0f1a]/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Built for <span className="text-[#00d4ff]">Trust & Compliance</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">Designed with the regulatory and fiduciary obligations of financial advisors in mind.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <TrustItem
              icon={Lock}
              title="No Custody"
              description="ProfitSpot is purely analytical. We never hold, manage, or transact with your clients' assets. Read-only data access only."
            />
            <TrustItem
              icon={Shield}
              title="Bank-Grade Security"
              description="Encrypted data in transit and at rest. SOC 2 compliance on our roadmap. Your client data is protected."
            />
            <TrustItem
              icon={FileText}
              title="Audit-Ready Reports"
              description="Every PDF report includes data sources, timestamps, disclaimers, and methodology — ready for compliance review."
            />
            <TrustItem
              icon={Globe}
              title="Multi-Chain Coverage"
              description="86+ blockchain networks, 6,500+ pools. Full coverage across Ethereum, Bitcoin DeFi, L2s, Solana, and emerging chains."
            />
          </div>
        </div>
      </section>


      {/* ═══ SECTION 7: CONTACT / DEMO FORM ═══ */}
      <section className="py-20 px-4" id="advisor-contact">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Schedule a <span className="text-[#00ff88]">Demo</span>
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto">Tell us about your firm and we'll show you how ProfitSpot fits into your advisory workflow. Response within 24 hours.</p>
          </div>

          <div className="bg-[#0f0f1a]/80 border border-gray-800 rounded-2xl p-8">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-[#00ff88]/15 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle className="w-8 h-8 text-[#00ff88]" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Thank You!</h3>
                <p className="text-gray-400 mb-6">We've received your request and will be in touch within 24 hours to schedule your personalized demo.</p>
                <button onClick={() => navigate('/explore')} className="inline-flex items-center gap-2 px-6 py-3 bg-gray-800 text-white text-sm font-bold rounded-xl hover:bg-gray-700 transition-all">
                  Explore the Platform
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5">Full Name *</label>
                    <input
                      type="text" name="full_name" required value={form.full_name} onChange={handleChange}
                      placeholder="John Smith"
                      className="w-full px-4 py-3 bg-[#0a0a12] border border-gray-700 rounded-xl text-white placeholder-gray-600 focus:border-[#00ff88] focus:outline-none transition-colors text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5">Email *</label>
                    <input
                      type="email" name="email" required value={form.email} onChange={handleChange}
                      placeholder="john@yourfirm.com"
                      className="w-full px-4 py-3 bg-[#0a0a12] border border-gray-700 rounded-xl text-white placeholder-gray-600 focus:border-[#00ff88] focus:outline-none transition-colors text-sm"
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5">Firm Name *</label>
                    <input
                      type="text" name="firm_name" required value={form.firm_name} onChange={handleChange}
                      placeholder="Smith Capital Advisors"
                      className="w-full px-4 py-3 bg-[#0a0a12] border border-gray-700 rounded-xl text-white placeholder-gray-600 focus:border-[#00ff88] focus:outline-none transition-colors text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1.5">AUM Range *</label>
                    <select
                      name="aum_range" required value={form.aum_range} onChange={handleChange}
                      className="w-full px-4 py-3 bg-[#0a0a12] border border-gray-700 rounded-xl text-white focus:border-[#00ff88] focus:outline-none transition-colors text-sm appearance-none cursor-pointer"
                    >
                      <option value="" disabled>Select AUM range</option>
                      <option value="Under $10M">Under $10M</option>
                      <option value="$10M-$50M">$10M – $50M</option>
                      <option value="$50M-$100M">$50M – $100M</option>
                      <option value="$100M-$500M">$100M – $500M</option>
                      <option value="$500M+">$500M+</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1.5">Message</label>
                  <textarea
                    name="message" rows={4} value={form.message} onChange={handleChange}
                    placeholder="Tell us about your firm's DeFi interest, client needs, or any specific questions..."
                    className="w-full px-4 py-3 bg-[#0a0a12] border border-gray-700 rounded-xl text-white placeholder-gray-600 focus:border-[#00ff88] focus:outline-none transition-colors text-sm resize-none"
                  />
                </div>
                {error && (
                  <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2.5">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 bg-[#00ff88] text-black font-bold text-base rounded-xl hover:bg-[#00cc6a] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Request a Demo
                    </>
                  )}
                </button>
                <p className="text-xs text-gray-600 text-center">We'll respond within 24 hours. No spam, no automated sequences.</p>
              </form>
            )}
          </div>
        </div>
      </section>


      {/* ═══ SECTION 8: FOOTER ═══ */}
      <footer className="border-t border-gray-800 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#00ff88] to-[#00d4ff] flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-black" />
                </div>
                <span className="text-lg font-bold">PROFIT<span className="text-[#00ff88]">SPOT</span></span>
              </div>
              <p className="text-sm text-gray-500">Non-custodial DeFi yield intelligence for financial advisors and RIA firms.</p>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Platform</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="/#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="/#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="/explore" className="hover:text-white transition-colors">Yield Explorer</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="/terms" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="/disclaimer" className="hover:text-white transition-colors">Risk Disclaimer</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="mailto:support@profitspot.live" className="hover:text-white transition-colors">support@profitspot.live</a></li>
                <li><a href="#advisor-contact" className="hover:text-white transition-colors">Schedule a Demo</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-600">© 2026 ProfitSpot — Built by Matthew Ball. A product of MGB Inc.</p>
            <p className="text-xs text-gray-700">Not financial advice. Cryptocurrency investments carry significant risk. DYOR. Past performance is not indicative of future results.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
